/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { MediaObjectCombinationRequest } from "@/src/types/Entities/MediaObject/MediaObjectCombinationRequest";
import { apiPatch } from "@/src/services/apiClient";
import { getProductData } from "@/src/services/productService";
import useMediaObjectCombinationRequest from "@/src/hooks/useMediaObjectCombinationRequest";
import { fetchCollection, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { useForm } from "@/src/provider/Form/FormProvider";
import { DocumentsFormConfig, getDocumentsFormConfig } from "@/src/services/documentsFormService";
import { MediaObject } from "@/src/types/Entities/MediaObject/MediaObject";
import { SectionElements, SectionElementsProps } from "./SectionElements";
import { SectionHeadline } from "./SectionHeadline";
import ProductBox from "@/src/components/ProductBox/ProductBox";

export type DocumentsFormProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};
export const DocumentsForm = ({ submitProps, product }: DocumentsFormProps) => {
  const [uploadIsBlocked, setUploadIsBlocked] = useState(false);
  const { values, updateValue, handleSubmit } = useForm();
  const queryClient = useQueryClient();
  const { requiredDocuments, optionalDocuments, headline, stepHeadline } = getDocumentsFormConfig({ product, submitProps });

  const urlParams = {
    product: product.name,
    productId: product.id,
  };

  function getDocumentTypes(documentFormConfig: DocumentsFormConfig[]) {
    return documentFormConfig
      .map((formConfig) => formConfig.mediaObjectTypeName || (formConfig.type === "FileUploadSection" ? formConfig.fieldName : formConfig.fieldname))
      .filter(Boolean);
  }

  const requiredDocumentTypes = getDocumentTypes(requiredDocuments);

  const optionalDocumentTypes = getDocumentTypes(optionalDocuments);

  const allTypes = [...requiredDocumentTypes, ...optionalDocumentTypes];

  const visibleFileUploadSections = [...requiredDocuments, ...optionalDocuments]
    .filter((document) => document.type === "FileUploadSection" && !document.isHidden?.({ product }))
    .map((document) => (document.type === "FileUploadSection" ? document.mediaObjectTypeName || document.fieldName : ""));

  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, updateAction, hasAtLeastOneFileOfType } = useMediaObjectCombinationRequest(
    product,
    visibleFileUploadSections
  );

  const productApiUri = getProductData(product.name, "apiUri");

  function refreshMediaObjectCollection() {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/media_objects"),
    });
  }

  async function refreshCase() {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.item(productApiUri, product.id),
    });
  }

  const { data: mediaObjectCollection, isLoading } = useQuery<MediaObject[]>({
    queryKey: queryKeys.collection("/media_objects", urlParams),
    queryFn: () =>
      fetchCollection("/media_objects", urlParams)
  });

  function checkIsReady() {
    const missingRequiredFiles = requiredDocumentTypes.filter(
      (type) =>
        ![
          ...(values.missingDocuments ?? []),
          ...product.mediaObjectCombinationRequests
            ?.filter((mediaObjectCombinationRequest: MediaObjectCombinationRequest) => allTypes.includes(mediaObjectCombinationRequest.type))
            .map((mediaObjectCombinationRequest: MediaObjectCombinationRequest) => {
              if (mediaObjectCombinationRequest.filesToCombine?.[0].files.length) {
                return mediaObjectCombinationRequest.type;
              }
            }),
          ...(mediaObjectCollection ?? []).filter((mediaObject) => allTypes.includes(mediaObject.fieldname)).map((mediaObject) => mediaObject.fieldname),
        ].includes(type)
    );

    return !isLoadingMediaObjectCombinationRequest && missingRequiredFiles.length === 0;
  }

  async function handleMissingDocumentCheckboxChange(documentType: string) {
    let newMissingDocuments: string[] = [];

    if (values?.missingDocuments?.includes(documentType)) {
      newMissingDocuments = [...values.missingDocuments.filter((docType: string) => docType !== documentType)];
    } else if (Array.isArray(values?.missingDocuments)) {
      newMissingDocuments = [...values.missingDocuments, documentType];
    } else {
      newMissingDocuments = [documentType];
    }

    await updateProductMissingDocuments(newMissingDocuments.filter((type) => requiredDocumentTypes.includes(type)));
  }

  async function updateProductMissingDocuments(newMissingDocuments: string[]) {
    const oldValues = { ...values };
    updateValue("missingDocuments", newMissingDocuments);

    await apiPatch(getProductData(product.name, "apiUri") + "/" + product.id, {
      missingDocuments: newMissingDocuments.filter(Boolean),
    }).catch(() => {
      updateValue("missingDocuments", oldValues?.missingDocuments);
    });
  }

  if (isLoading || isLoadingMediaObjectCombinationRequest || !mediaObjectCollection) {
    return <LegalbirdLoader centered />;
  }

  const sectionElementProps: Omit<SectionElementsProps, "configs"> = {
    product,
    mediaObjectCollection,
    mediaObjectCombinationRequests,
    updateAction,
    hasAtLeastOneFileOfType,
    onMissingDocumentsCheckboxChange: handleMissingDocumentCheckboxChange,
    refreshCase,
    refreshMediaObjectCollection,
    setUploadIsBlocked,
    uploadIsBlocked,
  };

  return (
    <ProductBox>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm, textAlign: "left" }} onSubmit={handleSubmit}>
          <Typography variant="h5" sx={(theme) => ({ color: theme.palette.text.primary, display: { xs: "block", sm: "none" }, textAlign: "left" })}>
            {stepHeadline}
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
              marginBottom: "15px",
            }}
          >
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>{headline}</FormLegend>
            </Grid>
            {Boolean(requiredDocuments.length) && (
              <>
                <SectionHeadline headline={"Notwendige Dokumente"} />
                {<SectionElements {...sectionElementProps} configs={requiredDocuments} />}
              </>
            )}
            {Boolean(optionalDocuments.length) && (
              <>
                {Boolean(requiredDocuments.length) && <SectionHeadline headline={"Weitere Dokumente"} marginTop={"48px"} />}
                {<SectionElements {...sectionElementProps} configs={optionalDocuments} />}
              </>
            )}
            <Grid size={12}>
              <FormSubmit {...submitProps} disabled={!checkIsReady()} sx={{ marginTop: "32px" }} />
            </Grid>
          </Grid>
        </Box>
      </FormFader>
    </ProductBox>
  );
};
